import { GetRowIdFunc, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import {
  DataLoadError,
  Modal,
  ModalWidth,
  AGTable as Table,
  AG_TABLE_HEADER_HEIGHT,
  AG_TABLE_ROW_HEIGHT,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { ReactElement, useCallback, useMemo, useRef } from 'react';
import useFetchDrillDown from '../../../../hooks/riskProducts/dashboard/useFetchDrillDown';
import { DrillDownDto } from '../../../../services/dtos/riskProducts/drillDown.dto';
import { RiskDistributionGroupBy } from '../../../../services/dtos/riskProducts/riskDistribution.dto';
import { WIDGET_ID } from '../../../../services/dtos/riskProducts/widgetMetric.dto';
import { useGlobalModalContext } from '../../../GlobalModal';
import NoResults from '../../../NoResults';
import { DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER, getDrillDownColumns } from './ChartDrillDownModal.constants';

import styles from './ChartDrillDownModal.module.scss';

const cx = classNames.bind(styles);

export interface ChartDrillDownModalProps {
  title: string;
  subHeader: string;
  companies: number[];
  groupBy?: RiskDistributionGroupBy;
  widget?: WIDGET_ID;
  metric?: string;
}

const DrillDownTable = Table<DrillDownDto>;

const ELEMENTS_ABOVE_TABLE_HEIGHT = 24;
const MAX_HEIGHT = 492;
const HEIGHT_FOR_NO_RESULTS = 160;

const ChartDrillDownModal = ({
  title,
  companies,
  groupBy,
  subHeader,
  widget,
  metric,
}: ChartDrillDownModalProps): ReactElement => {
  const { closeModal } = useGlobalModalContext();
  const { isLoading, isError, refetch, data } = useFetchDrillDown(companies);
  const gridApiRef = useRef<GridApi | null>(null);

  const columns = useMemo(() => getDrillDownColumns(groupBy, widget, metric), [groupBy, widget, metric]);
  const rowIdResolver = useCallback<GetRowIdFunc<DrillDownDto>>((params) => params.data.cs_companyid.toString(), []);

  const contentHeight = data?.length
    ? data.length * AG_TABLE_ROW_HEIGHT + AG_TABLE_HEADER_HEIGHT + ELEMENTS_ABOVE_TABLE_HEIGHT
    : HEIGHT_FOR_NO_RESULTS;

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
    event.columnApi.applyColumnState({ state: [{ colId: DEFAULT_SORT_FIELD, sort: DEFAULT_SORT_ORDER }] });
  }, []);

  const renderContent = () => {
    if (isError) {
      return <DataLoadError onRetryClick={refetch} />;
    }

    return (
      <>
        <div className={cx('header')} data-testid="header">
          <span className={cx('title')}>{subHeader}</span>
        </div>
        <div style={{ height: contentHeight > MAX_HEIGHT ? MAX_HEIGHT : contentHeight }}>
          <DrillDownTable
            columnDefs={columns}
            rowData={data}
            onGridReady={handleGridReady}
            noRowsOverlayComponent={NoResults}
            suppressMovableColumns
            getRowId={rowIdResolver}
          />
        </div>
      </>
    );
  };

  return (
    <Modal
      width={ModalWidth.Large}
      open
      onClose={closeModal}
      headerTitle={title}
      dataTestId="chart-drilldown-modal"
      isLoading={isLoading}
    >
      <div className={cx('body')}>{renderContent()}</div>
    </Modal>
  );
};

export default ChartDrillDownModal;
